import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Sprout, 
  TrendingUp, 
  Clock, 
  Calculator,
  Plus,
  Minus,
  RefreshCw,
  AlertTriangle,
  Info,
  Wallet,
  Percent,
  Zap
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';

interface FarmingPool {
  id: string;
  name: string;
  token0: string;
  token1: string;
  apy: number;
  tvl: number;
  rewardToken: string;
  dailyRewards: number;
  isActive: boolean;
}

interface FarmingPosition {
  id: string;
  poolId: string;
  poolName: string;
  token0: string;
  token1: string;
  lpAmount: number;
  lpValue: number;
  pendingRewards: number;
  rewardToken: string;
  impermanentLoss: number;
  autoCompound: boolean;
  depositedAt: string;
}

interface YieldCalculation {
  dailyYield: number;
  weeklyYield: number;
  monthlyYield: number;
  yearlyYield: number;
}

export default function YieldFarming() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'pools' | 'positions' | 'calculator'>('pools');
  const [selectedPool, setSelectedPool] = useState<FarmingPool | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<FarmingPosition | null>(null);
  const [showAddLiquidityModal, setShowAddLiquidityModal] = useState(false);
  const [showRemoveLiquidityModal, setShowRemoveLiquidityModal] = useState(false);
  const [showStakeModal, setShowStakeModal] = useState(false);
  const [showUnstakeModal, setShowUnstakeModal] = useState(false);
  const [token0Amount, setToken0Amount] = useState('');
  const [token1Amount, setToken1Amount] = useState('');
  const [removePercent, setRemovePercent] = useState('100');
  
  // Calculator state
  const [calcAmount, setCalcAmount] = useState('');
  const [calcApy, setCalcApy] = useState('');
  const [calcDays, setCalcDays] = useState('365');

  // Fetch farming pools
  const { data: pools, isLoading: loadingPools } = useQuery({
    queryKey: ['farming-pools'],
    queryFn: async () => {
      const response = await api.get('/crypto/farming/pools');
      return response.data as FarmingPool[];
    },
  });

  // Fetch farming positions
  const { data: positions, isLoading: loadingPositions } = useQuery({
    queryKey: ['farming-positions'],
    queryFn: async () => {
      const response = await api.get('/crypto/farming/positions');
      return response.data as FarmingPosition[];
    },
  });

  // Add liquidity mutation
  const addLiquidityMutation = useMutation({
    mutationFn: async (data: { poolId: string; token0Amount: number; token1Amount: number }) => {
      const response = await api.post('/crypto/farming/liquidity/add', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farming-positions'] });
      queryClient.invalidateQueries({ queryKey: ['farming-pools'] });
      setShowAddLiquidityModal(false);
      resetForm();
    },
  });

  // Remove liquidity mutation
  const removeLiquidityMutation = useMutation({
    mutationFn: async (data: { positionId: string; percent: number }) => {
      const response = await api.post(`/crypto/farming/liquidity/remove/${data.positionId}`, { percent: data.percent });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farming-positions'] });
      setShowRemoveLiquidityModal(false);
      setSelectedPosition(null);
      setRemovePercent('100');
    },
  });

  // Stake LP mutation
  const stakeLpMutation = useMutation({
    mutationFn: async (lpPositionId: string) => {
      const response = await api.post(`/crypto/farming/stake/${lpPositionId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farming-positions'] });
      setShowStakeModal(false);
    },
  });

  // Unstake LP mutation
  const unstakeLpMutation = useMutation({
    mutationFn: async (farmingPositionId: string) => {
      const response = await api.post(`/crypto/farming/unstake/${farmingPositionId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farming-positions'] });
      setShowUnstakeModal(false);
    },
  });

  // Harvest rewards mutation
  const harvestMutation = useMutation({
    mutationFn: async (positionId: string) => {
      const response = await api.post(`/crypto/farming/${positionId}/harvest`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farming-positions'] });
    },
  });

  // Toggle auto-compound mutation
  const toggleAutoCompoundMutation = useMutation({
    mutationFn: async (data: { positionId: string; enable: boolean }) => {
      const endpoint = data.enable 
        ? `/crypto/farming/${data.positionId}/auto-compound/enable`
        : `/crypto/farming/${data.positionId}/auto-compound/disable`;
      const response = await api.post(endpoint);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farming-positions'] });
    },
  });

  const resetForm = () => {
    setToken0Amount('');
    setToken1Amount('');
    setSelectedPool(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number, decimals = 4) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const handleAddLiquidity = () => {
    if (selectedPool && token0Amount && token1Amount) {
      addLiquidityMutation.mutate({
        poolId: selectedPool.id,
        token0Amount: parseFloat(token0Amount),
        token1Amount: parseFloat(token1Amount),
      });
    }
  };

  const handleRemoveLiquidity = () => {
    if (selectedPosition) {
      removeLiquidityMutation.mutate({
        positionId: selectedPosition.id,
        percent: parseFloat(removePercent),
      });
    }
  };

  // Calculate yield projections
  const calculateYield = (): YieldCalculation | null => {
    if (!calcAmount || !calcApy) return null;
    const amount = parseFloat(calcAmount);
    const apy = parseFloat(calcApy) / 100;
    const days = parseInt(calcDays);
    
    const dailyRate = apy / 365;
    const dailyYield = amount * dailyRate;
    const weeklyYield = amount * dailyRate * 7;
    const monthlyYield = amount * dailyRate * 30;
    const yearlyYield = amount * apy;
    
    return { dailyYield, weeklyYield, monthlyYield, yearlyYield };
  };

  // Calculate totals
  const totalLpValue = positions?.reduce((sum, p) => sum + p.lpValue, 0) || 0;
  const totalPendingRewards = positions?.reduce((sum, p) => sum + p.pendingRewards, 0) || 0;
  const totalIL = positions?.reduce((sum, p) => sum + (p.lpValue * p.impermanentLoss / 100), 0) || 0;

  const yieldProjection = calculateYield();

  return (
    <PageContainer
      title="Yield Farming"
      description="Forneça liquidez e ganhe recompensas em pools DeFi"
      icon={Sprout}
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Valor em LP</p>
                <p className="text-xl font-bold text-surface-100">{formatCurrency(totalLpValue)}</p>
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Recompensas Pendentes</p>
                <p className="text-xl font-bold text-success">{formatCurrency(totalPendingRewards)}</p>
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Impermanent Loss</p>
                <p className="text-xl font-bold text-warning">{formatCurrency(Math.abs(totalIL))}</p>
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent-500/20 flex items-center justify-center">
                <Sprout className="w-5 h-5 text-accent-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Posições Ativas</p>
                <p className="text-xl font-bold text-surface-100">{positions?.length || 0}</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'pools' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('pools')}
        >
          <Sprout className="w-4 h-4 mr-2" />
          Pools Disponíveis
        </Button>
        <Button
          variant={activeTab === 'positions' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('positions')}
        >
          <Wallet className="w-4 h-4 mr-2" />
          Minhas Posições
        </Button>
        <Button
          variant={activeTab === 'calculator' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('calculator')}
        >
          <Calculator className="w-4 h-4 mr-2" />
          Calculadora
        </Button>
      </div>

      {/* Pools Tab */}
      {activeTab === 'pools' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loadingPools ? (
            <div className="col-span-full text-center py-8 text-surface-400">Carregando...</div>
          ) : pools && pools.length > 0 ? (
            pools.map((pool) => (
              <Card key={pool.id} className="hover:border-primary-500/50 transition-colors">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-2">
                        <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center text-xs font-bold text-primary-400 border-2 border-surface-800">
                          {pool.token0.slice(0, 2)}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center text-xs font-bold text-accent-400 border-2 border-surface-800">
                          {pool.token1.slice(0, 2)}
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-surface-100">{pool.name}</h3>
                        <p className="text-xs text-surface-400">{pool.token0}/{pool.token1}</p>
                      </div>
                    </div>
                    {!pool.isActive && <Badge variant="warning">Inativo</Badge>}
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between">
                      <span className="text-surface-400 flex items-center gap-1">
                        <Percent className="w-4 h-4" /> APY
                      </span>
                      <span className="text-success font-bold text-lg">{pool.apy}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-surface-400">TVL</span>
                      <span className="text-surface-100">{formatCurrency(pool.tvl)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-surface-400">Recompensa</span>
                      <span className="text-surface-100">{pool.rewardToken}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-surface-400">Diário</span>
                      <span className="text-surface-100">{formatNumber(pool.dailyRewards, 2)} {pool.rewardToken}</span>
                    </div>
                  </div>

                  <Button
                    className="w-full"
                    disabled={!pool.isActive}
                    onClick={() => {
                      setSelectedPool(pool);
                      setShowAddLiquidityModal(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Liquidez
                  </Button>
                </div>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-surface-400">
              Nenhum pool de farming disponível
            </div>
          )}
        </div>
      )}

      {/* Positions Tab */}
      {activeTab === 'positions' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">Minhas Posições de Farming</h3>
            {loadingPositions ? (
              <div className="text-center py-8 text-surface-400">Carregando...</div>
            ) : positions && positions.length > 0 ? (
              <div className="space-y-4">
                {positions.map((position) => (
                  <div key={position.id} className="p-4 bg-surface-800 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="flex -space-x-2">
                          <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center text-sm font-bold text-primary-400 border-2 border-surface-800">
                            {position.token0.slice(0, 2)}
                          </div>
                          <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center text-sm font-bold text-accent-400 border-2 border-surface-800">
                            {position.token1.slice(0, 2)}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-surface-100">{position.poolName}</h4>
                          <p className="text-sm text-surface-400">{position.token0}/{position.token1}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {position.autoCompound && (
                          <Badge variant="success">
                            <Zap className="w-3 h-3 mr-1" />
                            Auto-Compound
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-surface-400">LP Tokens</p>
                        <p className="text-surface-100 font-medium">{formatNumber(position.lpAmount)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-surface-400">Valor</p>
                        <p className="text-surface-100 font-medium">{formatCurrency(position.lpValue)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-surface-400">Recompensas</p>
                        <p className="text-success font-medium">
                          +{formatNumber(position.pendingRewards)} {position.rewardToken}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-surface-400">IL</p>
                        <p className={`font-medium ${position.impermanentLoss < 0 ? 'text-error' : 'text-surface-100'}`}>
                          {formatPercent(position.impermanentLoss)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-surface-400">Desde</p>
                        <p className="text-surface-100 font-medium">{formatDate(position.depositedAt)}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {position.pendingRewards > 0 && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => harvestMutation.mutate(position.id)}
                          disabled={harvestMutation.isPending}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Colher Recompensas
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleAutoCompoundMutation.mutate({
                          positionId: position.id,
                          enable: !position.autoCompound,
                        })}
                        disabled={toggleAutoCompoundMutation.isPending}
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        {position.autoCompound ? 'Desativar' : 'Ativar'} Auto-Compound
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedPosition(position);
                          setShowRemoveLiquidityModal(true);
                        }}
                      >
                        <Minus className="w-4 h-4 mr-2" />
                        Remover Liquidez
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-surface-400">
                Você não possui posições de farming
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Calculator Tab */}
      {activeTab === 'calculator' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary-400" />
                Calculadora de Rendimento
              </h3>
              <div className="space-y-4">
                <Input
                  type="number"
                  label="Valor Investido (USD)"
                  placeholder="1000"
                  value={calcAmount}
                  onChange={(e) => setCalcAmount(e.target.value)}
                  icon={<Wallet className="w-4 h-4" />}
                />
                <Input
                  type="number"
                  label="APY (%)"
                  placeholder="50"
                  value={calcApy}
                  onChange={(e) => setCalcApy(e.target.value)}
                  icon={<Percent className="w-4 h-4" />}
                />
                <Input
                  type="number"
                  label="Período (dias)"
                  placeholder="365"
                  value={calcDays}
                  onChange={(e) => setCalcDays(e.target.value)}
                  icon={<Clock className="w-4 h-4" />}
                />
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-success" />
                Projeção de Rendimento
              </h3>
              {yieldProjection ? (
                <div className="space-y-4">
                  <div className="p-4 bg-surface-800 rounded-lg">
                    <p className="text-sm text-surface-400 mb-1">Rendimento Diário</p>
                    <p className="text-2xl font-bold text-success">{formatCurrency(yieldProjection.dailyYield)}</p>
                  </div>
                  <div className="p-4 bg-surface-800 rounded-lg">
                    <p className="text-sm text-surface-400 mb-1">Rendimento Semanal</p>
                    <p className="text-2xl font-bold text-success">{formatCurrency(yieldProjection.weeklyYield)}</p>
                  </div>
                  <div className="p-4 bg-surface-800 rounded-lg">
                    <p className="text-sm text-surface-400 mb-1">Rendimento Mensal</p>
                    <p className="text-2xl font-bold text-success">{formatCurrency(yieldProjection.monthlyYield)}</p>
                  </div>
                  <div className="p-4 bg-surface-800 rounded-lg">
                    <p className="text-sm text-surface-400 mb-1">Rendimento Anual</p>
                    <p className="text-2xl font-bold text-success">{formatCurrency(yieldProjection.yearlyYield)}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-surface-400">
                  Preencha os campos para ver a projeção
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Add Liquidity Modal */}
      <Modal
        isOpen={showAddLiquidityModal}
        onClose={() => {
          setShowAddLiquidityModal(false);
          resetForm();
        }}
        title={`Adicionar Liquidez - ${selectedPool?.name}`}
      >
        {selectedPool && (
          <div className="space-y-4">
            <div className="p-4 bg-surface-800 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-surface-400">Pool</span>
                <span className="text-surface-100">{selectedPool.token0}/{selectedPool.token1}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">APY</span>
                <span className="text-success font-bold">{selectedPool.apy}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">TVL</span>
                <span className="text-surface-100">{formatCurrency(selectedPool.tvl)}</span>
              </div>
            </div>

            <Input
              type="number"
              label={`Quantidade de ${selectedPool.token0}`}
              placeholder="0.0000"
              value={token0Amount}
              onChange={(e) => setToken0Amount(e.target.value)}
              step="0.0001"
            />

            <Input
              type="number"
              label={`Quantidade de ${selectedPool.token1}`}
              placeholder="0.0000"
              value={token1Amount}
              onChange={(e) => setToken1Amount(e.target.value)}
              step="0.0001"
            />

            <div className="flex items-start gap-2 p-3 bg-info/10 rounded-lg">
              <Info className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
              <p className="text-sm text-info">
                Ao fornecer liquidez, você receberá LP tokens que representam sua participação no pool.
                Esses tokens podem ser usados para farming e ganhar recompensas adicionais.
              </p>
            </div>

            <div className="flex gap-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddLiquidityModal(false);
                  resetForm();
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddLiquidity}
                disabled={!token0Amount || !token1Amount || addLiquidityMutation.isPending}
                className="flex-1"
              >
                {addLiquidityMutation.isPending ? 'Processando...' : 'Adicionar Liquidez'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Remove Liquidity Modal */}
      <Modal
        isOpen={showRemoveLiquidityModal}
        onClose={() => {
          setShowRemoveLiquidityModal(false);
          setSelectedPosition(null);
          setRemovePercent('100');
        }}
        title="Remover Liquidez"
      >
        {selectedPosition && (
          <div className="space-y-4">
            <div className="p-4 bg-surface-800 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-surface-400">Pool</span>
                <span className="text-surface-100">{selectedPosition.poolName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">LP Tokens</span>
                <span className="text-surface-100">{formatNumber(selectedPosition.lpAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Valor Atual</span>
                <span className="text-surface-100">{formatCurrency(selectedPosition.lpValue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Recompensas Pendentes</span>
                <span className="text-success">
                  {formatNumber(selectedPosition.pendingRewards)} {selectedPosition.rewardToken}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Porcentagem a Remover
              </label>
              <div className="flex gap-2 mb-2">
                {['25', '50', '75', '100'].map((pct) => (
                  <Button
                    key={pct}
                    variant={removePercent === pct ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setRemovePercent(pct)}
                  >
                    {pct}%
                  </Button>
                ))}
              </div>
              <Input
                type="number"
                value={removePercent}
                onChange={(e) => setRemovePercent(e.target.value)}
                min="1"
                max="100"
                step="1"
              />
            </div>

            {selectedPosition.impermanentLoss < 0 && (
              <div className="flex items-start gap-2 p-3 bg-warning/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <p className="text-sm text-warning">
                  Esta posição tem um impermanent loss de {formatPercent(selectedPosition.impermanentLoss)}.
                  Considere manter a posição se espera que os preços voltem ao equilíbrio.
                </p>
              </div>
            )}

            <div className="flex gap-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRemoveLiquidityModal(false);
                  setSelectedPosition(null);
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleRemoveLiquidity}
                disabled={removeLiquidityMutation.isPending}
                className="flex-1"
              >
                {removeLiquidityMutation.isPending ? 'Processando...' : `Remover ${removePercent}%`}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}
